import constants from "~/constants"

export default {
  // Generic methods
  get(key: string) {
    try {
      const value = localStorage.getItem(key)
      if (value) {
        return JSON.parse(value)
      } else {
        return null
      }
    } catch (e) {
      console.log("Storage", key, e)
      return null
    }
  },

  set(key: string, value: any) {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch (e) {
      console.log("Storage", key, e)
    }
  },

  remove(key: string) {
    try {
      localStorage.removeItem(key)
    } catch (e) {
      console.log("Storage", key, e)
    }
  },

  // Specific & explicit methods
  getAuthToken() {
    return this.get(constants.AUTH_TOKEN_KEY)
  },

  setAuthToken(value: string | null) {
    return this.set(constants.AUTH_TOKEN_KEY, value)
  },

  clearAuthToken() {
    return this.remove(constants.AUTH_TOKEN_KEY)
  },

  getAuthModule() {
    return this.get(constants.AUTH_MODULE)
  },

  setAuthModule(value: string) {
    return this.set(constants.AUTH_MODULE, value)
  },

  getCurrentUser() {
    return this.get(constants.USER_CURRENT)
  },

  setCurrentUser(value: Record<string, any> | null) {
    return this.set(constants.USER_CURRENT, value)
  },

  clearCurrentUser() {
    return this.remove(constants.USER_CURRENT)
  },

  clearSmartTopicsGradeSelect() {
    return this.remove(constants.SMART_TOPICS_GRADES_SELECT)
  },

  getTeacherFilteredJournals() {
    return this.get(constants.TEACHER_FILTERED_JOURNALS) || []
  },

  setTeacherFilteredJournals(value: string[]) {
    return this.set(constants.TEACHER_FILTERED_JOURNALS, value)
  },

  getStudentFilteredSubjects() {
    return this.get(constants.STUDENT_FILTERED_SUBJECTS) || []
  },

  setStudentFilteredSubjects(value: string[]) {
    return this.set(constants.STUDENT_FILTERED_SUBJECTS, value)
  },

  getTeacherCollapsedJournals() {
    return this.get(constants.TEACHER_COLLAPSED_JOURNALS) || []
  },

  setTeacherCollapsedJournals(value: number[]) {
    return this.set(constants.TEACHER_COLLAPSED_JOURNALS, value)
  },

  getStudentCollapsedLessons() {
    return this.get(constants.STUDENT_COLLAPSED_LESSONS) || []
  },

  setStudentCollapsedLessons(value: number[]) {
    return this.set(constants.STUDENT_COLLAPSED_LESSONS, value)
  },

  setSmartTopicsSubjectGradeFilter(subjectSlug: string, value: any) {
    return this.set(`${constants.SMART_TOPICS_SUBJECT_GRADE_FILTER}:${subjectSlug}`, value)
  },

  getSmartTopicsSubjectGradeFilter(subjectSlug: string) {
    return this.get(`${constants.SMART_TOPICS_SUBJECT_GRADE_FILTER}:${subjectSlug}`) || []
  },

  setQuestionBankSubjectGradeFilter(subjectSlug: string, value: any) {
    return this.set(`${constants.QUESTION_BANK_SUBJECT_GRADE_FILTER}:${subjectSlug}`, value)
  },

  getQuestionBankSubjectGradeFilter(subjectSlug: string) {
    return this.get(`${constants.QUESTION_BANK_SUBJECT_GRADE_FILTER}:${subjectSlug}`) || []
  },

  setAllMaterialsSubjectGradeFilter(subjectSlug: string, value: any) {
    return this.set(`${constants.ALL_MATERIALS_SUBJECT_GRADE_FILTER}:${subjectSlug}`, value)
  },

  getAllMaterialsSubjectGradeFilter(subjectSlug: string) {
    return this.get(`${constants.ALL_MATERIALS_SUBJECT_GRADE_FILTER}:${subjectSlug}`) || []
  },

  setProductFilters(productType: string, value: any) {
    const slugsOnly = {
      categories: value.categories?.map((c: any) => c.slug) || [],
      grades: value.grades?.map((g: any) => g.slug) || [],
      subjects: value.subjects?.map((s: any) => s.slug) || [],
      curriculumTypes: value.curriculumTypes?.map((c: any) => c.slug) || [],
    }
    return this.set(`${constants.PRODUCT_FILTERS_PREFIX}:${productType}`, slugsOnly)
  },

  getProductFilters(productType: string) {
    return this.get(`${constants.PRODUCT_FILTERS_PREFIX}:${productType}`)
  },

  setMyJournalsCollapsed(value: string[]) {
    return this.set(constants.MY_JOURNALS_COLLAPSED, value)
  },

  getMyJournalsCollapsed() {
    return this.get(constants.MY_JOURNALS_COLLAPSED) || []
  },

  setMyJournalsFilters(value: Record<number, string[]>) {
    return this.set(constants.MY_JOURNALS_FILTERS, value)
  },

  getMyJournalsFilters() {
    return this.get(constants.MY_JOURNALS_FILTERS) || {}
  },

  setStudentJournalsCollapsed(value: number[]) {
    return this.set(constants.STUDENT_JOURNALS_COLLAPSED, value)
  },

  getStudentJournalsCollapsed() {
    return this.get(constants.STUDENT_JOURNALS_COLLAPSED) || []
  },

  setStudentJournalsCollapsedTests(value: number[]) {
    return this.set(constants.STUDENT_JOURNALS_COLLAPSED_TESTS, value)
  },

  getStudentJournalsCollapsedTests() {
    return this.get(constants.STUDENT_JOURNALS_COLLAPSED_TESTS) || []
  },

  setStudentJournalsFilters(value: number[]) {
    return this.set(constants.STUDENT_JOURNALS_FILTERS, value)
  },

  getStudentJournalsFilters() {
    return this.get(constants.STUDENT_JOURNALS_FILTERS) || []
  },

  getTeacherCollapsedWorksheetJournalsWithProducts() {
    return this.get(constants.TEACHER_COLLAPSED_WORKSHEET_JOURNALS) || []
  },

  setTeacherCollapsedWorksheetJournalsWithProducts(value: number[]) {
    return this.set(constants.TEACHER_COLLAPSED_WORKSHEET_JOURNALS, value)
  },

  getTeacherFilteredWorksheetJournals() {
    return this.get(constants.TEACHER_FILTERED_WORKSHEET_JOURNALS) || []
  },

  setTeacherFilteredWorksheetJournals(value: string[]) {
    return this.set(constants.TEACHER_FILTERED_WORKSHEET_JOURNALS, value)
  },

  getStudentCollapsedWorksheetJournalsWithProducts() {
    return this.get(constants.STUDENT_COLLAPSED_WORKSHEET_JOURNALS) || []
  },

  setStudentCollapsedWorksheetJournalsWithProducts(value: number[]) {
    return this.set(constants.STUDENT_COLLAPSED_WORKSHEET_JOURNALS, value)
  },

  getStudentFilteredWorksheetSubjects() {
    return this.get(constants.STUDENT_FILTERED_WORKSHEET_SUBJECTS) || []
  },

  setStudentFilteredWorksheetSubjects(value: string[]) {
    return this.set(constants.STUDENT_FILTERED_WORKSHEET_SUBJECTS, value)
  },

  getQuestionBankSelectedSubject() {
    return this.get(constants.QUESTION_BANK_SELECTED_SUBJECT)
  },

  setQuestionBankSelectedSubject(subject: any) {
    return this.set(constants.QUESTION_BANK_SELECTED_SUBJECT, subject.id)
  },

  getAllMaterialsSelectedSubject() {
    return this.get(constants.ALL_MATERIALS_SELECTED_SUBJECT)
  },

  setAllMaterialsSelectedSubject(subject: any) {
    return this.set(constants.ALL_MATERIALS_SELECTED_SUBJECT, subject.id)
  },

  getMyQuizzesSelectedSubject() {
    return this.get(constants.MY_QUIZZES_SELECTED_SUBJECT)
  },

  setMyQuizzesSelectedSubject(subject: any) {
    return this.set(constants.MY_QUIZZES_SELECTED_SUBJECT, subject.id)
  },

  getSomaQuizzesSelectedSubject() {
    return this.get(constants.SOMA_QUIZZES_SELECTED_SUBJECT)
  },

  setSomaQuizzesSelectedSubject(subject: any) {
    return this.set(constants.SOMA_QUIZZES_SELECTED_SUBJECT, subject.id)
  },

  setTeacherTestsOverviewFilteredJournals(journals: any) {
    return this.set(constants.TEACHER_TESTS_OVERVIEW_FILTERED_JOURNALS, journals)
  },

  setOverviewFilteredJournals(journals: any) {
    return this.set(constants.OVERVIEW_FILTERED_JOURNALS, journals)
  },

  getOverviewFilteredJournals() {
    return this.get(constants.OVERVIEW_FILTERED_JOURNALS)
  },

  getTeacherTestsOverviewFilteredJournals() {
    return this.get(constants.TEACHER_TESTS_OVERVIEW_FILTERED_JOURNALS)
  },

  setStudentTestsOverviewFilteredSubjects(subjects: any) {
    return this.set(constants.STUDENT_TESTS_OVERVIEW_FILTERED_SUBJECTS, subjects)
  },

  getStudentTestsOverviewFilteredSubjects() {
    return this.get(constants.STUDENT_TESTS_OVERVIEW_FILTERED_SUBJECTS)
  },

  setCookieConsent(consent: "all" | "required") {
    return this.set(constants.COOKIE_CONSENT, consent)
  },

  getCookieConsent() {
    return this.get(constants.COOKIE_CONSENT)
  },

  getStudentWheelJournalId() {
    return this.get(constants.STUDENT_WHEEL_JOURNAL_ID)
  },

  setStudentWheelJournalId(value: number) {
    return this.set(constants.STUDENT_WHEEL_JOURNAL_ID, value)
  },

  getNotices(): Record<string, Record<string, unknown>> {
    return this.get(constants.NOTICES) || {}
  },

  setNoticeState(id: string, state: Record<string, unknown>) {
    const all = this.getNotices()
    all[id] = { ...all[id], ...state }
    return this.set(constants.NOTICES, all)
  },
}
